import type { EntryKey } from "@pinia/colada";
import type { MaybeRefOrGetter } from "vue";
import type {
  AddProjectMemberInput,
  BudgetBreakdownEntry,
  BudgetStatus,
  ConfigureProjectBudgetInput,
  CreateProjectInput,
  UpdateProjectInput,
} from "@/api/projects.api";
import { useMutation, useQuery, useQueryCache } from "@pinia/colada";
import { toValue } from "vue";
import { projectsApi } from "@/api/projects.api";
import { MEMBERS_QUERY_KEYS } from "@/queries/members.query";
import { SUBSCRIPTION_LIMITS_QUERY_KEYS } from "@/queries/subscription-limits.query";
import { $t } from "@/services/i18n";
import { useAuth } from "@/stores/auth.store";
import { useProjectStore } from "@/stores/project.store";
import { hasPermission } from "@/stores/user-profile.store";
import { getAllChargeSubtypes, getChargeSubtypeLabel } from "@/utils/charge-types";

/**
 * Query keys factory for projects following Pinia Colada best practices
 *
 * @see https://pinia-colada.esm.dev/guide/query-keys.html
 */
export const PROJECTS_QUERY_KEYS = {
  root: ["projects"] as const,
  list: () => [...PROJECTS_QUERY_KEYS.root, "list"] as const,
  detail: (id: string) => [...PROJECTS_QUERY_KEYS.root, "detail", id] as const,
  members: (projectId: string) => [...PROJECTS_QUERY_KEYS.root, "members", projectId] as const,
  budget: (projectId: string) => [...PROJECTS_QUERY_KEYS.root, "budget", projectId] as const,
  budgetStatus: (projectId: string) =>
    [...PROJECTS_QUERY_KEYS.root, "budget-status", projectId] as const,
  budgetBreakdown: (projectId: string, window: "short" | "long") =>
    [...PROJECTS_QUERY_KEYS.root, "budget-breakdown", projectId, window] as const,
};

// ==================== Queries ====================

/**
 * Query hook for fetching all projects the user has access to
 *
 * Features:
 * - Smart caching with 5min stale time
 * - Automatic deduplication of concurrent requests
 * - Enabled only for users with projects:read permission
 *
 * @example
 * ```ts
 * const projectsQuery = useProjectsQuery();
 *
 * // In template
 * if (projectsQuery.isLoading.value) return <Spinner />;
 * const projects = projectsQuery.data.value;
 * ```
 */
export function useProjectsQuery() {
  return useQuery({
    key: PROJECTS_QUERY_KEYS.list() as unknown as EntryKey,
    query: async () => projectsApi.list(),
    enabled: () => hasPermission("projects:read"),
    staleTime: 5 * 60_000, // 5 minutes - data is considered fresh
    gcTime: 10 * 60_000, // 10 minutes - garbage collection time
  });
}

/**
 * Query hook for fetching a single project's details
 *
 * @param id - The project ID to fetch (can be reactive ref or getter)
 *
 * @example
 * ```ts
 * const projectId = computed(() => route.params.id as string);
 * const projectQuery = useProjectQuery(projectId);
 * const project = projectQuery.data.value;
 * ```
 */
export function useProjectQuery(id: MaybeRefOrGetter<string>) {
  return useQuery({
    key: () => PROJECTS_QUERY_KEYS.detail(toValue(id)) as unknown as EntryKey,
    query: async () => projectsApi.getById(toValue(id)),
    enabled: () => !!toValue(id) && hasPermission("projects:read"),
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });
}

/**
 * Query hook for fetching project members
 *
 * @param projectId - The project ID to fetch members for (can be reactive ref or getter)
 */
export function useProjectMembersQuery(projectId: MaybeRefOrGetter<string>) {
  return useQuery({
    key: () => PROJECTS_QUERY_KEYS.members(toValue(projectId)) as unknown as EntryKey,
    query: async () => projectsApi.getMembers(toValue(projectId)),
    enabled: () =>
      !!toValue(projectId) && (hasPermission("projects:read") || hasPermission("team:read")),
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });
}

/**
 * Query hook for fetching project budget
 *
 * @param projectId - The project ID to fetch budget for (can be reactive ref or getter)
 */
export function useProjectBudgetQuery(projectId: MaybeRefOrGetter<string>) {
  return useQuery({
    key: () => PROJECTS_QUERY_KEYS.budget(toValue(projectId)) as unknown as EntryKey,
    query: async () => projectsApi.getBudget(toValue(projectId)),
    enabled: () => !!toValue(projectId) && hasPermission("projects:read"),
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });
}

/**
 * Query hook for fetching project budget status (role-scoped)
 *
 * @param projectId - The project ID to fetch budget status for
 */
export function useProjectBudgetStatusQuery(projectId: MaybeRefOrGetter<string>) {
  return useQuery({
    key: () => PROJECTS_QUERY_KEYS.budgetStatus(toValue(projectId)) as unknown as EntryKey,
    query: async () => projectsApi.getBudgetStatus(toValue(projectId)),
    enabled: () => !!toValue(projectId),
    staleTime: 30_000, // 30 seconds - budget status updates after each billable action
    gcTime: 5 * 60_000,
  });
}

/**
 * Query hook for fetching a project's budget consumption breakdown (by charge type / model).
 *
 * Lazy by default via `enabled` - meant to be triggered on demand (e.g. popover open).
 *
 * @param projectId - The project ID to fetch the breakdown for
 * @param window - Which budget window to break down ("short" or "long")
 * @param enabled - Extra reactive gate on top of the projectId check (default: always enabled)
 */
export function useProjectBudgetBreakdownQuery(
  projectId: MaybeRefOrGetter<string>,
  window: MaybeRefOrGetter<"short" | "long">,
  enabled: MaybeRefOrGetter<boolean> = true
) {
  return useQuery({
    key: () =>
      PROJECTS_QUERY_KEYS.budgetBreakdown(toValue(projectId), toValue(window)) as unknown as EntryKey,
    query: async () => projectsApi.getBudgetBreakdown(toValue(projectId), { window: toValue(window) }),
    enabled: () => !!toValue(projectId) && toValue(enabled),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
}

// ==================== Invalidation Helpers ====================

/**
 * Helper hook to invalidate projects queries
 *
 * Use this after mutations that affect projects
 *
 * @example
 * ```ts
 * const invalidate = useInvalidateProjects();
 *
 * const handleProjectUpdate = async () => {
 *   await updateProject(...);
 *   invalidate.all();
 * };
 * ```
 */
export function useInvalidateProjects() {
  const queryCache = useQueryCache();

  return {
    /**
     * Invalidate all projects queries
     */
    all: () => queryCache.invalidateQueries({ key: PROJECTS_QUERY_KEYS.root as any }),

    /**
     * Invalidate only projects list query
     */
    list: () => queryCache.invalidateQueries({ key: PROJECTS_QUERY_KEYS.list() as any }),

    /**
     * Invalidate a specific project's detail query
     */
    detail: (id: string) =>
      queryCache.invalidateQueries({ key: PROJECTS_QUERY_KEYS.detail(id) as any }),

    /**
     * Invalidate a specific project's members query
     */
    members: (projectId: string) =>
      queryCache.invalidateQueries({ key: PROJECTS_QUERY_KEYS.members(projectId) as any }),

    /**
     * Invalidate a specific project's budget query
     */
    budget: (projectId: string) =>
      queryCache.invalidateQueries({ key: PROJECTS_QUERY_KEYS.budget(projectId) as any }),

    /**
     * Invalidate a specific project's budget status query
     */
    budgetStatus: (projectId: string) =>
      queryCache.invalidateQueries({ key: PROJECTS_QUERY_KEYS.budgetStatus(projectId) as any }),

    /**
     * Invalidate a specific project's budget breakdown queries (both windows, by key prefix)
     */
    budgetBreakdown: (projectId: string) =>
      queryCache.invalidateQueries({
        key: [...PROJECTS_QUERY_KEYS.root, "budget-breakdown", projectId] as any,
      }),
  };
}

/**
 * Invalidate budget status query after a billable action.
 * Delay accounts for async billing pipeline (server → RabbitMQ → billing-engine → DB).
 */
export function useInvalidateBudgetAfterBilling() {
  const invalidate = useInvalidateProjects();
  const projectStore = useProjectStore();

  return () => {
    const projectId = projectStore.selectedProjectId;
    if (!projectId) return;
    setTimeout(() => {
      invalidate.budgetStatus(projectId);
      invalidate.budgetBreakdown(projectId);
    }, 3000);
  };
}

// ==================== Mutations ====================

/**
 * Mutation to create a new project
 *
 * @example
 * ```ts
 * const createProject = useCreateProject();
 * const newProject = await createProject.mutateAsync({
 *   name: "Production",
 *   description: "Production environment"
 * });
 * ```
 */
export function useCreateProject() {
  const queryCache = useQueryCache();
  const auth = useAuth();

  return useMutation({
    mutation: async (body: CreateProjectInput) => projectsApi.create(body),
    onSuccess: () => {
      queryCache.invalidateQueries({ key: PROJECTS_QUERY_KEYS.root as any });
      queryCache.invalidateQueries({ key: SUBSCRIPTION_LIMITS_QUERY_KEYS.root as any });
      // Refresh user profile to update ProjectSelector dropdown
      auth.refreshUser();
    },
  });
}

/**
 * Mutation to update a project
 *
 * @example
 * ```ts
 * const updateProject = useUpdateProject();
 * await updateProject.mutateAsync({
 *   id: "project-123",
 *   body: { name: "New Name" }
 * });
 * ```
 */
export function useUpdateProject() {
  const queryCache = useQueryCache();
  const auth = useAuth();

  return useMutation({
    mutation: async ({ id, body }: { id: string; body: UpdateProjectInput }) =>
      projectsApi.update(id, body),
    onSuccess: () => {
      queryCache.invalidateQueries({ key: PROJECTS_QUERY_KEYS.root as any });
      // Refresh user profile to update ProjectSelector dropdown
      auth.refreshUser();
    },
  });
}

/**
 * Mutation to archive a project
 *
 * @example
 * ```ts
 * const archiveProject = useArchiveProject();
 * await archiveProject.mutateAsync("project-123");
 * ```
 */
export function useArchiveProject() {
  const queryCache = useQueryCache();
  const auth = useAuth();

  return useMutation({
    mutation: async (id: string) => projectsApi.archive(id),
    onSuccess: () => {
      queryCache.invalidateQueries({ key: PROJECTS_QUERY_KEYS.root as any });
      queryCache.invalidateQueries({ key: SUBSCRIPTION_LIMITS_QUERY_KEYS.root as any });
      // Refresh user profile to update ProjectSelector dropdown
      auth.refreshUser();
    },
  });
}

/**
 * Mutation to reactivate a previously archived project
 *
 * @example
 * ```ts
 * const reactivateProject = useReactivateProject();
 * await reactivateProject.mutateAsync("project-123");
 * ```
 */
export function useReactivateProject() {
  const queryCache = useQueryCache();
  const auth = useAuth();

  return useMutation({
    mutation: async (id: string) => projectsApi.reactivate(id),
    onSuccess: () => {
      queryCache.invalidateQueries({ key: PROJECTS_QUERY_KEYS.root as any });
      // Refresh user profile to update ProjectSelector dropdown
      auth.refreshUser();
    },
  });
}

// ==================== Member Mutations ====================

/**
 * Mutation to add a member to a project
 *
 * @example
 * ```ts
 * const addMember = useAddProjectMember();
 * await addMember.mutateAsync({
 *   projectId: "project-123",
 *   body: { user_id: "user-456" }
 * });
 * ```
 */
export function useAddProjectMember() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async ({ projectId, body }: { projectId: string; body: AddProjectMemberInput }) =>
      projectsApi.addMember(projectId, body),
    onSuccess: (_, { projectId }) => {
      queryCache.invalidateQueries({ key: PROJECTS_QUERY_KEYS.members(projectId) as any });
      queryCache.invalidateQueries({ key: PROJECTS_QUERY_KEYS.list() as any });
      queryCache.invalidateQueries({ key: MEMBERS_QUERY_KEYS.root as any });
      queryCache.invalidateQueries({ key: SUBSCRIPTION_LIMITS_QUERY_KEYS.root as any });
    },
  });
}

/**
 * Mutation to remove a member from a project
 *
 * @example
 * ```ts
 * const removeMember = useRemoveProjectMember();
 * await removeMember.mutateAsync({
 *   projectId: "project-123",
 *   memberId: "member-456"
 * });
 * ```
 */
export function useRemoveProjectMember() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async ({ projectId, memberId }: { projectId: string; memberId: string }) =>
      projectsApi.removeMember(projectId, memberId),
    onSuccess: (_, { projectId }) => {
      queryCache.invalidateQueries({ key: PROJECTS_QUERY_KEYS.members(projectId) as any });
      queryCache.invalidateQueries({ key: PROJECTS_QUERY_KEYS.list() as any });
      queryCache.invalidateQueries({ key: MEMBERS_QUERY_KEYS.root as any });
      queryCache.invalidateQueries({ key: SUBSCRIPTION_LIMITS_QUERY_KEYS.root as any });
    },
  });
}

// ==================== Budget Mutations ====================

/**
 * Mutation to configure a project's budget
 *
 * @example
 * ```ts
 * const configureBudget = useConfigureProjectBudget();
 * await configureBudget.mutateAsync({
 *   projectId: "project-123",
 *   body: {
 *     short_window_hours: 5,
 *     short_window_limit: 50,
 *     long_window_days: 7,
 *     long_window_limit: 500,
 *     alert_threshold_pct: 80,
 *     enforce_limit: true
 *   }
 * });
 * ```
 */
export function useConfigureProjectBudget() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async ({
      projectId,
      body,
    }: {
      projectId: string;
      body: ConfigureProjectBudgetInput;
    }) => projectsApi.configureBudget(projectId, body),
    onSuccess: (_, { projectId }) => {
      queryCache.invalidateQueries({ key: PROJECTS_QUERY_KEYS.budget(projectId) as any });
      queryCache.invalidateQueries({ key: PROJECTS_QUERY_KEYS.budgetStatus(projectId) as any });
      queryCache.invalidateQueries({
        key: [...PROJECTS_QUERY_KEYS.root, "budget-breakdown", projectId] as any,
      });
      queryCache.invalidateQueries({ key: PROJECTS_QUERY_KEYS.detail(projectId) as any });
      queryCache.invalidateQueries({ key: PROJECTS_QUERY_KEYS.list() as any });
    },
  });
}

/**
 * Mutation to remove a project's budget (back to unlimited)
 */
export function useRemoveProjectBudget() {
  const queryCache = useQueryCache();
  return useMutation({
    mutation: async (projectId: string) => projectsApi.removeBudget(projectId),
    onSuccess: (_, projectId) => {
      queryCache.invalidateQueries({ key: PROJECTS_QUERY_KEYS.budget(projectId) as any });
      queryCache.invalidateQueries({ key: PROJECTS_QUERY_KEYS.budgetStatus(projectId) as any });
      queryCache.invalidateQueries({
        key: [...PROJECTS_QUERY_KEYS.root, "budget-breakdown", projectId] as any,
      });
      queryCache.invalidateQueries({ key: PROJECTS_QUERY_KEYS.detail(projectId) as any });
      queryCache.invalidateQueries({ key: PROJECTS_QUERY_KEYS.list() as any });
    },
  });
}

// ==================== Helpers ====================

/**
 * Get display properties for a project status (translated)
 */
export function getProjectStatus(status?: string) {
  const statuses: Record<string, { label: string; color: "green" | "gray" | "red" }> = {
    active: { label: $t("active" as any), color: "green" },
    archived: { label: $t("archived" as any), color: "gray" },
    suspended: { label: $t("suspended" as any), color: "red" },
  };

  if (!status || !(status in statuses)) {
    return statuses.active;
  }
  return statuses[status];
}

/**
 * Calculate budget usage percentage
 */
export function getBudgetUsagePercentage(usage?: number, limit?: number | null): number | null {
  if (!limit || limit <= 0) return null;
  if (!usage || usage < 0) return 0;
  return Math.min(100, Math.round((usage / limit) * 100));
}

/**
 * Get budget usage color based on percentage
 */
export function getBudgetUsageColor(
  percentage: number | null
): "green" | "yellow" | "orange" | "red" {
  if (percentage === null) return "green";
  if (percentage < 50) return "green";
  if (percentage < 75) return "yellow";
  if (percentage < 90) return "orange";
  return "red";
}

export type BudgetIndicatorColor = "green" | "yellow" | "orange" | "red";

/**
 * Color thresholds for budget indicators.
 * Returns a Radix color name to use with data-accent.
 */
export function getBudgetIndicatorColor(percentage: number | null): BudgetIndicatorColor {
  if (percentage === null) return "green";
  if (percentage >= 100) return "red";
  if (percentage >= 90) return "orange";
  if (percentage >= 70) return "yellow";
  return "green";
}

/**
 * Get the most critical (highest) usage percentage from a BudgetStatus
 */
export function getBudgetMaxUsagePercentage(
  status: BudgetStatus | null | undefined
): number | null {
  if (!status) return null;
  const shortPct = status.short_window?.used_percentage ?? null;
  const longPct = status.long_window?.used_percentage ?? null;
  if (shortPct === null && longPct === null) return null;
  return Math.max(shortPct ?? 0, longPct ?? 0);
}

/**
 * Resolve the display label for a budget breakdown entry (top-level item or child).
 *
 * Top level (isTopLevel = true) represents a charge type: resolved against the
 * charge_subtypes catalog when the key is a known subtype, falling back to the
 * label the API sent for unrecognized keys.
 *
 * Second level (isTopLevel = false) represents a model: the key itself is the label,
 * except for the "unknown"/"other" sentinel keys shared with the top level.
 */
export function getBudgetBreakdownLabel(entry: BudgetBreakdownEntry, isTopLevel: boolean): string {
  if (entry.key === "unknown") return $t("projects.budget_breakdown_unknown");
  if (entry.key === "other") return $t("projects.budget_breakdown_other");
  if (isTopLevel) {
    const knownSubtypes: string[] = getAllChargeSubtypes();
    if (knownSubtypes.includes(entry.key)) {
      return getChargeSubtypeLabel(entry.key);
    }
    return entry.label;
  }
  return entry.label ?? entry.key;
}

/**
 * Format seconds into a human-readable duration (e.g., "2h 30m", "3d 12h")
 */
export function formatResetTime(seconds: number | null | undefined): string | null {
  if (seconds == null || seconds <= 0) return null;
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}
